<template>
    <div class="guided-claims-editor">
        <div class="guided-claims-editor__options guided-claims-editor__options--primary">
            <BFormCheckbox
                v-for="option in primaryOptions"
                :key="option.id"
                :checked="isSelected(option.id)"
                class="guided-claims-editor__option"
                :class="{ 'guided-claims-editor__option--selected': isSelected(option.id) }"
                @change="toggleClaim(option.id, $event)"
            >
                <strong>{{ option.label }}</strong>
            </BFormCheckbox>
        </div>

        <div class="guided-claims-editor__advanced mt-75">
            <BButton
                type="button"
                variant="flat-secondary"
                class="guided-claims-editor__advanced-toggle"
                :aria-expanded="advancedExpanded ? 'true' : 'false'"
                @click="advancedExpanded = !advancedExpanded"
            >
                <span>
                    {{ translate('Component(ResearchReviewProcess):other-claims', 'Other claims') }}
                    <BBadge v-if="advancedSelectedCount" variant="light-secondary" class="ml-50">
                        {{ advancedSelectedCount }}
                    </BBadge>
                </span>
                <FeatherIcon
                    :icon="advancedExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    size="15"
                />
            </BButton>

            <div v-show="advancedExpanded" class="guided-claims-editor__advanced-body">
                <div class="guided-claims-editor__options guided-claims-editor__options--advanced">
                    <BFormCheckbox
                        v-for="option in advancedOptions"
                        :key="option.id"
                        :checked="isSelected(option.id)"
                        class="guided-claims-editor__option"
                        :class="{ 'guided-claims-editor__option--selected': isSelected(option.id) }"
                        @change="toggleClaim(option.id, $event)"
                    >
                        {{ option.label }}
                    </BFormCheckbox>
                </div>

            </div>
        </div>

        <div v-if="selectedClaimRows.length" class="guided-claims-editor__evidence mt-75">
            <BButton
                type="button"
                variant="flat-secondary"
                class="guided-claims-editor__evidence-toggle"
                :aria-expanded="evidenceExpanded ? 'true' : 'false'"
                @click="evidenceExpanded = !evidenceExpanded"
            >
                <span>
                    {{ translate('Component(ResearchReviewProcess):claim-evidence', 'Claim evidence') }}
                    <BBadge v-if="claimsWithEvidenceCount" variant="light-secondary" class="ml-50">
                        {{ claimsWithEvidenceCount }}
                    </BBadge>
                </span>
                <FeatherIcon
                    :icon="evidenceExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    size="15"
                />
            </BButton>

            <div v-show="evidenceExpanded" class="guided-claims-editor__evidence-body">
                <small class="text-less-muted d-block mb-75">
                    {{ translate(
                        'Component(ResearchReviewProcess):claim-evidence-description',
                        'Evidence is optional. Add source text or a page reference when available.',
                    ) }}
                </small>
                <BFormGroup
                    v-for="row in selectedClaimRows"
                    :key="`claim-evidence-${row.index}`"
                    :label="claimLabel(row.claim.type)"
                    class="mb-75"
                >
                    <BFormTextarea
                        :value="row.claim.evidence"
                        rows="2"
                        max-rows="4"
                        :placeholder="translate(
                            'Component(ResearchReviewProcess):claim-evidence-placeholder',
                            'Text supporting this claim',
                        )"
                        @input="updateEvidence(row.index, $event || null)"
                    />
                </BFormGroup>
            </div>
        </div>
    </div>
</template>

<script>
import {
    BBadge,
    BButton,
    BFormCheckbox,
    BFormGroup,
    BFormTextarea,
} from 'bootstrap-vue'
import { CLAIM_TYPES } from '../regulation-review-options'

const PRIMARY_CLAIM_TYPES = Object.freeze([
    'compliant',
    'substances_below_threshold',
    'does_not_contain_restricted_substances',
])

const CONFLICTING_CLAIM_TYPES = Object.freeze({
    compliant: ['non_compliant'],
    non_compliant: ['compliant'],
    substances_above_threshold: ['no_substances_above_threshold'],
    no_substances_above_threshold: ['substances_above_threshold'],
    contains_restricted_substances: ['does_not_contain_restricted_substances'],
    does_not_contain_restricted_substances: ['contains_restricted_substances'],
    has_exemptions: ['no_exemptions'],
    no_exemptions: ['has_exemptions'],
})

const GUIDED_CLAIM_TYPES = Object.freeze(CLAIM_TYPES.slice())
const isGuidedClaim = claim => claim && GUIDED_CLAIM_TYPES.includes(claim.type)

export default {
    name: 'GuidedClaimsEditor',
    components: {
        BBadge,
        BButton,
        BFormCheckbox,
        BFormGroup,
        BFormTextarea,
    },
    props: {
        claims: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            advancedExpanded: false,
            evidenceExpanded: false,
        }
    },
    computed: {
        primaryOptions() {
            return PRIMARY_CLAIM_TYPES.map(this.createOption)
        },
        advancedOptions() {
            return GUIDED_CLAIM_TYPES
                .filter(type => !PRIMARY_CLAIM_TYPES.includes(type))
                .map(this.createOption)
        },
        advancedSelectedCount() {
            return this.advancedOptions.filter(option => this.isSelected(option.id)).length
        },
        selectedClaimRows() {
            return this.claims.reduce((rows, claim, index) => {
                if (isGuidedClaim(claim)) {
                    rows.push({ claim, index })
                }
                return rows
            }, [])
        },
        claimsWithEvidenceCount() {
            return this.selectedClaimRows.filter(row => (
                typeof row.claim.evidence === 'string' && row.claim.evidence.trim().length > 0
            )).length
        },
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
        humanize(value) {
            return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase())
        },
        claimLabel(type) {
            return this.translate(
                `Component(ResearchReviewProcessEnum):claim-type-${type}`,
                this.humanize(type),
            )
        },
        createOption(type) {
            return { id: type, label: this.claimLabel(type) }
        },
        isSelected(type) {
            return this.claims.some(claim => claim && claim.type === type)
        },
        normalizeClaims(claims) {
            return claims.filter(isGuidedClaim).map(claim => {
                const normalized = { ...claim }
                delete normalized.value
                return normalized
            })
        },
        toggleClaim(type, selected) {
            let claims = this.claims.slice()

            if (!selected) {
                claims = claims.filter(claim => !claim || claim.type !== type)
                this.$emit('input', this.normalizeClaims(claims))
                return
            }

            const conflicts = CONFLICTING_CLAIM_TYPES[type] || []
            claims = claims.filter(claim => claim && !conflicts.includes(claim.type))

            if (!claims.some(claim => claim && claim.type === type)) {
                claims.push({ type, evidence: null })
            }

            this.$emit('input', this.normalizeClaims(claims))
        },
        updateEvidence(index, evidence) {
            const claims = this.claims.map((claim, claimIndex) => (
                claimIndex === index ? { ...claim, evidence } : claim
            ))

            this.$emit('input', this.normalizeClaims(claims))
        },
    },
}
</script>

<style lang="scss" scoped>
.guided-claims-editor__options {
    display: grid;
    gap: .75rem;
}

.guided-claims-editor__options--primary {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.guided-claims-editor__options--advanced {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.guided-claims-editor__option {
    min-height: 3.5rem;
    margin: 0;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    cursor: pointer;
    transition: border-color .15s ease, background-color .15s ease;

    &.custom-checkbox.custom-control {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1.1rem 1rem;
    }

    &--selected {
        border-color: #f58049;
        background: rgba(245, 128, 73, .04);
    }

    ::v-deep .custom-control-label {
        display: inline-flex;
        align-items: center;
        width: auto;
        min-height: 1.3rem;
        padding-left: 2.5rem;
        cursor: pointer;
    }

    ::v-deep .custom-control-label::before,
    ::v-deep .custom-control-label::after {
        top: 50%;
        left: 0;
        margin-left: .75rem;
        transform: translateY(-50%);
    }
}

.guided-claims-editor__advanced,
.guided-claims-editor__evidence {
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.guided-claims-editor__advanced-toggle,
.guided-claims-editor__evidence-toggle {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: .75rem 1rem;
    border: 0;
    color: #4a455b !important;
    background: transparent !important;
    box-shadow: none !important;
    text-align: left;

    &:hover,
    &:focus,
    &:active {
        color: #4a455b !important;
        background: transparent !important;
        box-shadow: none !important;
    }

    &:focus-visible {
        outline: 2px solid rgba(245, 128, 73, .35);
        outline-offset: -2px;
    }
}

.guided-claims-editor__advanced-body,
.guided-claims-editor__evidence-body {
    padding: .75rem;
    border-top: 1px solid #ebe9f1;
}

.guided-claims-editor__evidence-body .form-group:last-child {
    margin-bottom: 0 !important;
}

@media (max-width: 767.98px) {
    .guided-claims-editor__options--primary,
    .guided-claims-editor__options--advanced {
        grid-template-columns: 1fr;
    }
}
</style>
